import { type AvailabilityInfo } from "~/domains/product/api/pna/AvailabilityInfo";
import { type PackagingInformationEntry } from "~/domains/product/types/ProductData";
import { type LineItemMessage } from "./common";
import { PSSizeMode, selectBestPackBasedOnStock } from "../cartHelper";
import { DST_SAP_MAT_NO, INVALID_PACKAGE_CODE } from "../constants";
import { PackageEntrySource } from "~/features/cart/cart/models/CartData";
import { type LineItemBuilder } from "./LineItemUpdate";
import { getPackageCodes } from "~/domains/product/utils/productDescriptorUtils";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";

export const MSG_MISSING_PACKAGE_CONTRACT: LineItemMessage = {
  type: "fatal",
  key: "cartPage.error.contract.missingPackage",
};
export const MSG_MISSING_PACKAGE_REGULAR: LineItemMessage = {
  type: "fatal",
  key: "cartPage.error.regular.missingPackage",
};
export const MSG_COULD_NOT_PRESELECT_PACKAGE: LineItemMessage = {
  type: "fatal",
  key: "cartPage.bulkUpload.packageChoiceError",
};
export const MSG_PACKAGE_CHANGED: LineItemMessage = {
  type: "info",
  key: "cartPage.bulkUpload.packageChanged",
};

export function refreshAvailabilityLoadContract(lineItem: LineItemBuilder, availability: AvailabilityInfo): void {
  lineItem.removeMessage(MSG_MISSING_PACKAGE_CONTRACT.key);
  if (lineItem.get("source") === PackageEntrySource.CONTRACT) {
    const packageExists = validatePackage(lineItem, availability);
    if (!packageExists) {
      lineItem.addMessage(MSG_MISSING_PACKAGE_CONTRACT);
    }
  }
}

export function refreshAvailabilityLoadNonContract(
  lineItem: LineItemBuilder,
  availability: AvailabilityInfo,
  productDescriptor: ProductDescriptor,
): void {
  lineItem.removeMessage(MSG_COULD_NOT_PRESELECT_PACKAGE.key);
  lineItem.removeMessage(MSG_PACKAGE_CHANGED.key);
  lineItem.removeMessage(MSG_MISSING_PACKAGE_REGULAR.key);

  switch (lineItem.get("source")) {
    case PackageEntrySource.BULK: {
      const packageExists = validatePackage(lineItem, availability);
      let packageData: PackagingInformationEntry | undefined;
      if (packageExists) {
        packageData = availability.packagingInformation.find(
          (packageData) => packageData.packCode === lineItem.get("packCode"),
        );
      } else {
        if (lineItem.get("restrictedPackage")) {
          lineItem.addMessage(MSG_MISSING_PACKAGE_REGULAR);
          break;
        }
        packageData = selectBestPackBasedOnStock(
          lineItem.get("quantity"),
          getPackageCodes(productDescriptor),
          productDescriptor,
          PSSizeMode.SMALLEST_FIRST,
        );
      }

      if (!packageData) {
        lineItem.addMessage(MSG_COULD_NOT_PRESELECT_PACKAGE);
        break;
      }
      refreshPackage(lineItem, packageData);
      if (lineItem.isFieldUpdated("packCode") && availability.packagingInformation.length > 1) {
        lineItem.addMessage(MSG_PACKAGE_CHANGED);
      }
      break;
    }

    case PackageEntrySource.DEFAULT: {
      if (lineItem.get("packCode") === INVALID_PACKAGE_CODE) {
        const packageData = selectBestPackBasedOnStock(
          lineItem.get("quantity"),
          getPackageCodes(productDescriptor),
          productDescriptor,
          PSSizeMode.SMALLEST_FIRST,
        );
        if (!packageData) {
          // SCHAEFF-16083 - Treat "XX" package code as PMA product, so "XX" is not an invalid/error causing code anymore
          // lineItem.addMessage(MSG_MISSING_PACKAGE_REGULAR);
          break;
        }
        refreshPackage(lineItem, packageData);
        if (lineItem.isFieldUpdated("packCode") && availability.packagingInformation.length > 1) {
          lineItem.addMessage(MSG_PACKAGE_CHANGED);
        }
      } else {
        // Verify that package is still available
        const packageExists = validatePackage(lineItem, availability);

        if (!packageExists) {
          lineItem.addMessage(MSG_MISSING_PACKAGE_REGULAR);
          break;
        }
      }

      break;
    }

    default: {
      throw new Error("unsupported");
    }
  }
}

export function validatePackage(lineItem: LineItemBuilder, availabilityInfo: AvailabilityInfo): boolean {
  const isDST = availabilityInfo.sapMaterialNumber === DST_SAP_MAT_NO;
  return isDST
    ? true
    : availabilityInfo.packagingInformation
        // SCHAEFF-16083 - Treat "XX" package code as PMA product, so "XX" is not an invalid/error causing code anymore
        // .filter((pkg) => pkg.packCode !== INVALID_PACKAGE_CODE)
        .some((packageInfo) => packageInfo.packCode === lineItem.get("packCode"));
}

export function refreshPackage(lineItem: LineItemBuilder, packageInfo: PackagingInformationEntry): void {
  if (lineItem.get("packCode") !== packageInfo.packCode) {
    lineItem.set("packCode", packageInfo.packCode);
  }
  if (lineItem.get("packSize") !== packageInfo.packSize) {
    lineItem.set("packSize", packageInfo.packSize);
  }
  if (lineItem.get("primaryWarehouseCode") !== packageInfo.primaryWarehouseCode) {
    lineItem.set("primaryWarehouseCode", packageInfo.primaryWarehouseCode);
  }
  const primaryWarehouseName = packageInfo.totalWarehouse.name;
  if (primaryWarehouseName && lineItem.get("primaryWarehouseName") !== primaryWarehouseName) {
    lineItem.set("primaryWarehouseName", primaryWarehouseName);
  }

  if (lineItem.get("source") !== PackageEntrySource.DEFAULT) {
    lineItem.set("source", PackageEntrySource.DEFAULT);
  }
}
